/**
 * EmailRow — single thread row inside the inbox list.
 *
 * Pure render. All state lives in EmailPage; this component is a presentation
 * shell with click + selection handlers passed in.
 *
 * Extracted from EmailPage.js (Iteration 104, Batch 3 component split).
 */
import React from 'react';

// Lead-stage chip — colored by status
const STAGE_CHIP = {
  new: 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300',
  contacted: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  qualified: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
  working: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
  converted: 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300',
  closed_won: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
  closed_lost: 'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300',
  unqualified: 'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300',
};
const stageChipClass = (s) => STAGE_CHIP[s] || 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300';

export default function EmailRow({
  thread,
  folder,
  isSelected,
  isOpen,
  isExpanded,
  formatDate,
  onSelect,
  onToggleSelect,
  onToggleExpand,
}) {
  const email = thread.latest;
  return (
    <div
      className={`group border-b border-slate-100 dark:border-slate-700 transition cursor-pointer relative ${
        isOpen ? 'bg-blue-50 dark:bg-blue-900/30' : 'hover:bg-slate-50 dark:hover:bg-slate-700/50'
      } ${!email.is_read ? 'bg-blue-50/40 dark:bg-blue-900/10' : ''}`}
      data-testid={`email-item-${email.id}`}
      onClick={() => onSelect(email)}
    >
      {!email.is_read && <div className="absolute left-0 top-0 bottom-0 w-[3px] bg-blue-500" />}

      <div className="flex items-start gap-2 p-3 pl-4">
        <input
          type="checkbox"
          checked={isSelected}
          onChange={(e) => { e.stopPropagation(); onToggleSelect(email.id); }}
          onClick={(e) => e.stopPropagation()}
          className="mt-0.5 h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 flex-shrink-0"
          data-testid={`row-checkbox-${email.id}`}
        />

        <div className="flex-1 min-w-0">
          {/* Top line: sender + thread-count + date */}
          <div className="flex items-center gap-2">
            <span className={`text-sm truncate flex-1 ${!email.is_read ? 'font-bold text-slate-900 dark:text-white' : 'text-slate-700 dark:text-slate-300'}`}>
              {folder === 'sent' ? (email.to_recipients?.[0] || '—') : (email.from_name || email.from_email || '—')}
            </span>
            {thread.count > 1 && (
              <span
                className="px-1.5 py-0.5 text-[10px] font-semibold rounded bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300 flex-shrink-0 cursor-pointer hover:bg-slate-300 dark:hover:bg-slate-600"
                title={`${thread.count} messages in conversation — click to ${isExpanded ? 'collapse' : 'expand'}`}
                data-testid={`thread-count-${thread.key}`}
                onClick={(e) => { e.stopPropagation(); onToggleExpand(thread.key); }}
              >
                {thread.count}
              </span>
            )}
            <span className="text-xs text-slate-400 flex-shrink-0">
              {formatDate(email.received_at || email.sent_at || email.synced_at || email.created_at)}
            </span>
          </div>

          <p className={`text-sm truncate mt-0.5 ${!email.is_read ? 'font-semibold text-slate-800 dark:text-slate-100' : 'text-slate-600 dark:text-slate-400'}`}>
            {email.subject || '(no subject)'}
          </p>
          <p className="text-xs text-slate-400 truncate mt-0.5">{email.body_preview}</p>

          {/* Chip row */}
          <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
            {email.lead_name && (
              <span
                className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium rounded bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300 max-w-[160px]"
                title={`Linked lead: ${email.lead_name}`}
                data-testid={`row-lead-chip-${email.id}`}
              >
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                <span className="truncate">{email.lead_name}</span>
              </span>
            )}
            {email.lead_status && (
              <span
                className={`px-1.5 py-0.5 text-[10px] font-medium rounded uppercase tracking-wide ${stageChipClass(email.lead_status)}`}
                title={`Lead stage: ${email.lead_status}`}
                data-testid={`row-stage-chip-${email.id}`}
              >
                {String(email.lead_status).replace(/_/g, ' ')}
              </span>
            )}
            {email.has_attachments && (
              <span className="text-slate-400" title="Has attachments">
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" /></svg>
              </span>
            )}
            {email.starred && (
              <span className="text-yellow-500" title="Starred">
                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" /></svg>
              </span>
            )}
            {folder === 'sent' && email.tracking_id && email.open_count > 0 && (
              <span className="inline-flex items-center gap-0.5 text-green-600 text-[10px]" title={`Opened ${email.open_count} time(s)`}>
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                {email.open_count}
              </span>
            )}
            {folder === 'sent' && email.tracking_id && email.click_count > 0 && (
              <span className="inline-flex items-center gap-0.5 text-blue-600 text-[10px]" title={`${email.click_count} link click(s)`} data-testid={`row-click-count-${email.id}`}>
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" /></svg>
                {email.click_count}
              </span>
            )}
            {email.follow_up_at && new Date(email.follow_up_at) <= new Date() && (
              <span
                className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-medium rounded bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300"
                title={`Follow-up reminder fired ${new Date(email.follow_up_at).toLocaleString()}`}
                data-testid={`row-followup-due-${email.id}`}
              >
                <svg className="w-2.5 h-2.5" fill="currentColor" viewBox="0 0 20 20"><path d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zM10 18a3 3 0 01-3-3h6a3 3 0 01-3 3z" /></svg>
                Awaiting reply
              </span>
            )}
            {email.snoozed_until && new Date(email.snoozed_until) > new Date() && (
              <span
                className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-medium rounded bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300"
                title={`Snoozed until ${new Date(email.snoozed_until).toLocaleString()}`}
                data-testid={`row-snoozed-${email.id}`}
              >
                <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                Snoozed
              </span>
            )}
          </div>

          {/* Inline-expanded thread */}
          {isExpanded && thread.count > 1 && (
            <div className="mt-2 ml-2 pl-3 border-l-2 border-slate-200 dark:border-slate-600 space-y-1">
              {thread.items.slice(1).map((older) => (
                <button
                  key={older.id}
                  type="button"
                  onClick={(e) => { e.stopPropagation(); onSelect(older); }}
                  className="w-full text-left text-xs text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 truncate"
                  data-testid={`thread-older-${older.id}`}
                >
                  <span className="text-slate-400">{formatDate(older.received_at || older.sent_at)}</span>
                  <span className="ml-2">{older.from_name || older.from_email}</span>
                  <span className="ml-2 text-slate-400">— {older.body_preview?.slice(0, 60)}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
